import React, { useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
} from "@mui/material";

import Input from "../../../../components/inputs/Input";
import {
  useGetModels,
  useGetPolicesDevicesBySponsor,
} from "../../../../services/microservice/micelu/device/use-device";
import { IModel } from "../../../../services/microservice/micelu/device/device-service.interface";
import { useCreateContext } from "../provider/create.provider";
import capitalizeFirstLetter from "../../../../util/toCapitalize";
import { useLoginUser } from "../../../../hooks/useLoginUser";

const DeviceComponent = ({
  control,
  watch,
  setValue,
  errors,
  clearErrors,
  reloadDevices,
}) => {
  const { isLogin } = useLoginUser();
  const [models, setModels] = useState<IModel[]>([]);
  const [error, setError] = useState<string>("");

  const { mutateAsync: getPolicies, isLoading: loadingPolicies } =
    useGetPolicesDevicesBySponsor();
  const { infoPolicies, setInfoPolicies, setReferenceTac } = useCreateContext();
  const { mutateAsync: getModels } = useGetModels("SAMSUNG");

  const deviceVariant = watch("deviceVariant");
  const insuredValue = watch("insuredValue");
  const imei = watch("imei");

  const variantLabelById: Record<string, string> = {
    s26: "Samsung S26 Base",
    s26_plus: "Samsung S26 Plus",
    s26_ultra: "Samsung S26 Ultra",
  };

  const getVariantIdFromName = (deviceName: string) => {
    const normalized = deviceName.toUpperCase().replaceAll(/\s+/g, "");
    if (normalized.includes("ULTRA")) return "s26_ultra";
    if (normalized.includes("PLUS")) return "s26_plus";
    return "s26";
  };

  const getCapacityId = (capacityGb?: string) => {
    if (!capacityGb) return "";
    return capacityGb === "1024" ? "1tb" : `${capacityGb}gb`;
  };

  const getCapacityLabel = (capacityGb?: string) => {
    if (!capacityGb) return "";
    return capacityGb === "1024" ? "1 TB" : `${capacityGb} GB`;
  };

  const getCapacityCompactLabel = (capacityGb?: string) => {
    if (!capacityGb) return "";
    return capacityGb === "1024" ? "1TB" : `${capacityGb}GB`;
  };

  const deviceVariantOptions = useMemo(() => {
    return (models || []).map((model) => {
      const variantId = getVariantIdFromName(model.device_name);
      const variantLabel =
        variantLabelById[variantId] || capitalizeFirstLetter(variantId);
      return {
        id: String(model.device_id),
        label: `${variantLabel} ${getCapacityCompactLabel(model.capacity_gb)}`,
      };
    });
  }, [models]);

  const groupOrder = [
    "Samsung S26 Base",
    "Samsung S26 Plus",
    "Samsung S26 Ultra",
  ];

  const groupedOptions = useMemo(() => {
    const groups: Record<string, typeof deviceVariantOptions> = {};
    deviceVariantOptions.forEach((option) => {
      const variant = option.label.includes("Ultra")
        ? "Samsung S26 Ultra"
        : option.label.includes("Plus")
          ? "Samsung S26 Plus"
          : "Samsung S26 Base";
      if (!groups[variant]) groups[variant] = [];
      groups[variant].push(option);
    });
    return groups;
  }, [deviceVariantOptions]);

  const selectedModel = useMemo(
    () =>
      (models || []).find(
        (model) => String(model.device_id) === deviceVariant?.id,
      ),
    [models, deviceVariant],
  );

  useEffect(() => {
    if (!isLogin) return;
    getModels("SAMSUNG").then((res) => {
      const filtered = res.data.models.filter(
        (model) =>
          model.device_name.includes("S26") &&
          model.device_name.toUpperCase().includes("ULTRA"),
      );
      setModels(filtered);
    });
  }, [isLogin, reloadDevices]);

  useEffect(() => {
    if (selectedModel) {
      setValue("storageCapacity", {
        id: getCapacityId(selectedModel.capacity_gb),
        label: getCapacityLabel(selectedModel.capacity_gb),
      } as any);
      setValue("insuredValue", String(selectedModel.insured_value || ""));
    } else {
      setValue("storageCapacity", "" as any);
      setValue("insuredValue", "");
    }
  }, [selectedModel]);

  useEffect(() => {
    if (
      imei &&
      imei?.length === 15 &&
      insuredValue &&
      deviceVariant &&
      selectedModel
    ) {
      getPlansDevices();
    } else {
      setValue("plan", "" as any);
      setValue("valuePolicie", "");
      clearErrors("plan");
      setInfoPolicies(null);
    }
  }, [deviceVariant, insuredValue, imei, selectedModel]);

  useEffect(() => {
    if (insuredValue === "$") {
      setValue("insuredValue", "");
    }
  }, [insuredValue]);

  const getPlansDevices = async () => {
    try {
      if (selectedModel && insuredValue && isLogin) {
        const res = await getPolicies({
          imei,
          tac: selectedModel.reference_tac,
          insuredValue: insuredValue?.replace(/,/g, "").replace("$", ""),
          manufacturer: selectedModel.manufacturer,
          model: selectedModel.device_name,
          sponsorID: "SAMSUNG",
        });

        const silverPolicies = res.data.policies.filter((d) =>
          d.name.includes("ilver"),
        );

        if (!silverPolicies.length) {
          setInfoPolicies([]);
          setValue("plan", "" as any);
          setValue("valuePolicie", "");
          return;
        }

        setValue("plan", silverPolicies[0].pricingOptions[0].id as any);
        setValue(
          "valuePolicie",
          `$ ${silverPolicies[0].pricingOptions[0].paymentAmountWithTaxes
            .toString()
            .replaceAll(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,")}`,
        );
        setInfoPolicies(silverPolicies);
        setReferenceTac(selectedModel.reference_tac);
        setError("");
      } else {
        setInfoPolicies(null);
        setValue("plan", "" as any);
        setValue("valuePolicie", "");
      }
    } catch (error) {
      if (error.status === 400) {
        setError(error?.response?.data?.error?.message);
      } else {
        setError("Ocurrio un error inesperado");
      }
    }
  };

  return (
    <section className="w-full">
      <div className="mb-10 w-full">
        <div className="space-y-6">
          <div className="rounded-2xl border-2 border-gray bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-lg font-semibold">Datos dispositivo</h2>
            <div className="grid gap-6 md:grid-cols-1">
              <Controller
                name="imei"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    classNameContainer="w-full mb-7"
                    classNameInput="w-full"
                    placeholder="IMEI"
                    required
                    error={errors?.imei?.message}
                  />
                )}
              />
              <Controller
                name="deviceVariant"
                control={control}
                render={({ field }) => (
                  <FormControl component="fieldset">
                    <p className="my-0 py-0 text-base">
                      Modelo del dispositivo{" "}
                      <span className="text-center text-3xl text-critical">
                        *
                      </span>
                      {errors?.deviceVariant && (
                        <span className="my-0 py-0 text-base text-critical">
                          {errors?.deviceVariant?.id?.message}
                        </span>
                      )}
                    </p>
                    {/* ✅ Usar solo el id como value, no JSON */}
                    <RadioGroup
                      value={field.value?.id ?? ""}
                      onChange={(e) => {
                        const selected = deviceVariantOptions.find(
                          (o) => o.id === e.target.value,
                        );
                        field.onChange(
                          selected
                            ? { id: selected.id, label: selected.label }
                            : null,
                        );
                      }}
                    >
                      <div className="mt-2 md:space-y-4">
                        {groupOrder
                          .filter((g) => groupedOptions[g])
                          .map((groupName) => (
                            <div
                              key={groupName}
                              className="border-b border-surface"
                            >
                              <div className="flex flex-col sm:flex-row sm:flex-wrap md:gap-1">
                                {groupedOptions[groupName].map((option) => (
                                  <FormControlLabel
                                    key={option.id}
                                    value={option.id}
                                    control={<Radio />}
                                    label={option.label}
                                  />
                                ))}
                              </div>
                            </div>
                          ))}
                      </div>
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </div>
          </div>

          <div className="rounded-2xl border-2 border-gray bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-lg font-semibold">Datos del plan</h2>
            {loadingPolicies ? (
              <Stack>
                {[...Array(infoPolicies?.length || 1)].map((_, i) => (
                  <Stack
                    key={i}
                     sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "flex-start",
                          }}
                  >
                    <Skeleton
                            variant="text"
                            width="80%"
                            sx={{ marginLeft: "12px" }}
                          />
                  </Stack>
                ))}
              </Stack>
            ) : infoPolicies?.length > 0 ? (
              <>
                {infoPolicies.map((policy, idx) => (
                  <dl
                    key={idx}
                    className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2"
                  >
                    <div>
                      <dt className="text-gray-500 text-sm">Nombre del plan</dt>
                      <dd className="font-medium">
                        Garantía Extendida 12 Meses
                      </dd>
                    </div>
                  </dl>
                ))}
              </>
            ) : (
              <p className="my-0 mb-10 py-0 text-base">
                No hay planes disponibles
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeviceComponent;
